import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useTeamInfo } from '../../../team-id-context';
import { getDealsForTeam } from '../../../api/queries';
import { getDocumentsByDeal, searchDocumentsByTeam } from '../../../api/queries_v2';
import { ReactComponent as FolderIcon } from '../../../shared/icons/folder-icon.svg';
import { ReactComponent as DocumentIcon } from '../../../shared/icons/document-icon.svg';
import { ReactComponent as BackIcon } from '../../../shared/icons/chevron-left.svg';
import { ReactComponent as SearchIcon } from '../../../shared/icons/search-icon.svg';
import LoadingSpinner from '../../loading-spinner';
import styles from './DocumentBrowser.module.scss';

interface DocumentBrowserProps {
  onDocumentSelect: (documentId: string, documentName: string) => void;
  currentDealId?: string;
}

interface Deal {
  id: string;
  title: string;
}

interface Document {
  id: string;
  title: string;
}

export const DocumentBrowser: React.FC<DocumentBrowserProps> = ({ 
  onDocumentSelect,
  currentDealId 
}) => {
  // Initialize with currentDealId if provided
  const [selectedDealId, setSelectedDealId] = useState<string | null>(currentDealId || null);
  const [selectedDealName, setSelectedDealName] = useState<string>('');
  const [isDealLoading, setIsDealLoading] = useState<boolean>(!!currentDealId);
  const [searchQuery, setSearchQuery] = useState('');
  const teamInfo = useTeamInfo();
  const teamId = teamInfo.teamId;

  // Get deals for the team
  const { data: dealsData, loading: dealsLoading } = useQuery(getDealsForTeam, {
    variables: { teamId },
    skip: !teamId,
  });

  // Get documents for selected deal
  const { data: documentsData, loading: documentsLoading } = useQuery(getDocumentsByDeal, {
    variables: { dealId: selectedDealId },
    skip: !selectedDealId,
  });

  // Search all documents when there's a search query (search globally regardless of current view)
  const { data: searchData, loading: searchLoading, error: _searchError } = useQuery(searchDocumentsByTeam, {
    variables: { teamId, searchQuery },
    skip: !teamId || !searchQuery,  // Always search when there's a query
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });


  const deals: Deal[] = useMemo(() => dealsData?.dealsForTeam || [], [dealsData]);
  const documents: Document[] = useMemo(() => documentsData?.getDocumentsByDeal || [], [documentsData]);
  const searchResults = useMemo(() => {
    const results = searchData?.searchDocumentsByTeam || [];
    return results;
  }, [searchData]);

  // When currentDealId is provided and deals are loaded, set the deal name
  useEffect(() => {
    if (currentDealId && deals.length > 0 && !selectedDealName) {
      const currentDeal = deals.find(deal => deal.id === currentDealId);
      if (currentDeal) {
        setSelectedDealName(currentDeal.title);
        setIsDealLoading(false);
      }
    } else if (!currentDealId) {
      setIsDealLoading(false);
    }
  }, [currentDealId, deals, selectedDealName]);

  // When searching globally, use search results; otherwise show documents within selected deal
  const _displayDocuments = searchQuery 
    ? searchResults 
    : documents;

  const handleDocumentClick = (document: Document) => {
    onDocumentSelect(document.id, document.title);
  };

  const handleDealClick = (deal: Deal) => {
    setSelectedDealId(deal.id);
    setSelectedDealName(deal.title);
    setSearchQuery(''); // Clear search when entering deal
  };

  const handleBackToDeal = () => {
    setSelectedDealId(null);
    setSelectedDealName('');
    setSearchQuery('');
  };

  // Show loading while resolving initial deal
  if (isDealLoading || (dealsLoading && !deals.length)) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner />
      </div>
    );
  }
  

  return (
    <div className={styles.documentBrowser}>
      {/* Search Bar */}
      <div className={styles.searchContainer}>
        <div className={styles.searchBox}>
          <SearchIcon />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {/* Breadcrumb - sticky when in a deal */}
      {selectedDealId && (
        <div className={styles.breadcrumb}>
          <button className={styles.backButton} onClick={handleBackToDeal}>
            <BackIcon />
            <span>Back to Deals</span>
          </button>
          <span className={styles.breadcrumbSeparator}>|</span>
          <span className={styles.breadcrumbCurrent}>{selectedDealName}</span>
        </div>
      )}

      {/* Content */}
      <div className={styles.content}>
        {!selectedDealId ? (
          // Main View - Show deals or search results
          <>
            {searchQuery ? (
              // When searching, show ONLY documents
              searchLoading ? (
                <div className={styles.loading}>
                  <LoadingSpinner />
                </div>
              ) : searchResults.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No documents found</p>
                </div>
              ) : (
                <div className={styles.itemsList}>
                  {searchResults.map((doc: any) => (
                    <div
                      key={`doc-${doc.id}`}
                      className={styles.itemRow}
                      onClick={() => handleDocumentClick({ id: doc.id, title: doc.title })}
                    >
                      <div className={styles.itemIcon}>
                        <DocumentIcon />
                      </div>
                      <div className={styles.itemContent}>
                        <div className={styles.itemName}>{doc.title}</div>
                        <div className={styles.itemType}>in {doc.deal?.title}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              // When not searching, show deals as folders
              deals.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No deals available</p>
                </div>
              ) : (
                <div className={styles.itemsList}>
                  {deals.map((deal) => (
                    <div
                      key={`deal-${deal.id}`}
                      className={styles.itemRow}
                      onClick={() => handleDealClick(deal)}
                    >
                      <div className={styles.itemIcon}>
                        <FolderIcon />
                      </div>
                      <div className={styles.itemContent}>
                        <div className={styles.itemName}>{deal.title}</div>
                        <div className={styles.itemType}>Deal</div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </>
        ) : (
          // Document List View
          <>
            {searchQuery ? (
              // When searching from inside a deal, show global search results
              searchLoading ? (
                <div className={styles.loading}>
                  <LoadingSpinner />
                </div>
              ) : searchResults.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No documents found</p>
                </div>
              ) : (
                <div className={styles.itemsList}>
                  {searchResults.map((document: any) => (
                    <div
                      key={document.id}
                      className={styles.itemRow}
                      onClick={() => handleDocumentClick(document)}
                    >
                      <div className={styles.itemIcon}>
                        <DocumentIcon />
                      </div>
                      <div className={styles.itemContent}>
                        <div className={styles.itemName}>{document.title}</div>
                        <div className={styles.itemType}>in {document.deal?.title}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              // When not searching, show documents in this deal
              documentsLoading ? (
                <div className={styles.loading}>
                  <LoadingSpinner />
                </div>
              ) : documents.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No documents in this deal</p>
                </div>
              ) : (
                <div className={styles.itemsList}>
                  {documents.map((document) => (
                    <div
                      key={document.id}
                      className={styles.itemRow}
                      onClick={() => handleDocumentClick(document)}
                    >
                      <div className={styles.itemIcon}>
                        <DocumentIcon />
                      </div>
                      <div className={styles.itemContent}>
                        <div className={styles.itemName}>{document.title}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};